'use client';

import { useEffect, useRef } from 'react';

import {
  GA_EVENTS,
  GA_PARAMS,
  GA_SECTION_VIEW_IDS,
  GA_SECTION_VIEW_THRESHOLD,
  type GaChapterId,
} from '@/constants/analytics';
import { trackEvent } from '@/lib/analytics/trackEvent';

/**
 * Fires `section_view` once per chapter id per page load when a section is
 * ≥50% visible. Separate from StickyNav scroll-spy (45% top marker).
 */
export function SectionViewTracker() {
  const seenRef = useRef<Set<GaChapterId>>(new Set());

  useEffect(() => {
    const sections = GA_SECTION_VIEW_IDS.flatMap((id) => {
      const el = document.getElementById(id);
      return el ? [{ id, el }] : [];
    });

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < GA_SECTION_VIEW_THRESHOLD) {
            continue;
          }
          const id = entry.target.id as GaChapterId;
          if (!GA_SECTION_VIEW_IDS.includes(id) || seenRef.current.has(id)) continue;
          seenRef.current.add(id);
          trackEvent(GA_EVENTS.sectionView, {
            [GA_PARAMS.chapterId]: id,
          });
        }
      },
      { threshold: [GA_SECTION_VIEW_THRESHOLD] },
    );

    for (const { el } of sections) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  return null;
}
